import React, { useState } from 'react';
import { Button, Icon, H1, StatusBadge } from '@tviklax/ui-components';
import { useGraphQLQuery } from '@tviklax/ui-components';
import { gql } from '@apollo/client';
import { TrackingData } from './types';
import {
  TrackingContainer,
  PageHeader,
  Subtitle,
  SearchSection,
  SearchForm,
  TrackingCard,
  CardHeader,
  TrackingNumber,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  Timeline,
  TimelineItem,
  EventTime,
  EventLocation,
  EventDescription,
} from './styles';

const ORDER_SHIPMENTS_QUERY = gql`
  query OrderShipments($limit: Int = 100, $offset: Int = 0) {
    orderShipments(limit: $limit, offset: $offset) {
      id
      trackingNumber
      shippingProvider
      shippingStatus
      location
      shippingOrigin
      createdAt
      updatedAt
    }
  }
`;

const sampleTracking: TrackingData = {
  trackingNumber: 'TRK123456789',
  carrier: 'DHL Express',
  status: 'In Transit',
  estimatedDelivery: '2024-01-15',
  origin: 'New York, NY',
  destination: 'Los Angeles, CA',
  currentLocation: 'Chicago, IL',
  events: [
    {
      timestamp: '2024-01-12 14:30',
      location: 'Chicago, IL',
      status: 'In Transit',
      description: 'Package is in transit to next facility',
    },
    {
      timestamp: '2024-01-12 08:15',
      location: 'Cleveland, OH',
      status: 'Departed Facility',
      description: 'Package has departed from facility',
    },
    {
      timestamp: '2024-01-11 22:45',
      location: 'Cleveland, OH',
      status: 'Arrived at Facility',
      description: 'Package arrived at sorting facility',
    },
    {
      timestamp: '2024-01-11 18:20',
      location: 'New York, NY',
      status: 'Picked Up',
      description: 'Package picked up by carrier',
    },
  ],
};

const getTrackingStatusVariant = (status: string) => {
  const normalized = status.toLowerCase().replace(/\s+/g, '-');
  switch (normalized) {
    case 'delivered':
      return 'delivered';
    case 'out-for-delivery':
      return 'out-for-delivery';
    case 'in-transit':
      return 'in-transit';
    case 'processing':
      return 'processing';
    default:
      return 'neutral';
  }
};

interface OrderShipment {
  id: string;
  trackingNumber?: string;
  shippingProvider?: string;
  shippingStatus?: number;
  location?: string;
  shippingOrigin?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const Tracking: React.FC = () => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingData, setTrackingData] = useState<TrackingData | null>(sampleTracking);
  const { data } = useGraphQLQuery<{ orderShipments: OrderShipment[] }>(ORDER_SHIPMENTS_QUERY, {
    variables: { limit: 200, offset: 0 },
    showErrorToast: false,
  });

  const mapStatus = (statusCode?: number): TrackingData['status'] => {
    if (statusCode === 7) return 'Delivered';
    if (statusCode === 5) return 'Out for Delivery';
    if (statusCode === 0 || statusCode === 1 || statusCode === 2 || statusCode === 3) return 'Processing';
    return 'In Transit';
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!trackingNumber) {
      return;
    }

    const liveShipment = (data?.orderShipments || []).find(
      (row) => (row.trackingNumber || '').toLowerCase() === trackingNumber.toLowerCase()
    );

    if (liveShipment) {
      const liveTracking: TrackingData = {
        trackingNumber: liveShipment.trackingNumber || trackingNumber,
        carrier: liveShipment.shippingProvider || 'Unknown',
        status: mapStatus(liveShipment.shippingStatus),
        estimatedDelivery: liveShipment.updatedAt || liveShipment.createdAt || new Date().toISOString(),
        origin: liveShipment.shippingOrigin || 'N/A',
        destination: 'N/A',
        currentLocation: liveShipment.location || 'N/A',
        events: [
          {
            timestamp: liveShipment.updatedAt || liveShipment.createdAt || new Date().toISOString(),
            location: liveShipment.location || 'N/A',
            status: mapStatus(liveShipment.shippingStatus),
            description: 'Live shipment status from backend',
          },
        ],
      };
      setTrackingData(liveTracking);
      return;
    }

    // Fallback for demo/sandbox scenarios where no matching DB record is present.
    setTrackingData(sampleTracking);
  };

  return (
    <TrackingContainer>
      <PageHeader initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <H1 style={{ margin: '0' }}>Tracking</H1>
        <Subtitle>Track your shipments in real-time</Subtitle>
      </PageHeader>

      <SearchSection initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <SearchForm onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Enter tracking number (e.g., TRK123456789)"
            value={trackingNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTrackingNumber(e.target.value)}
            style={{
              flex: 1,
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <Button type="submit">
            <Icon name="Search" size={16} />
            Track
          </Button>
        </SearchForm>
      </SearchSection>

      {trackingData && (
        <TrackingCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <CardHeader>
            <TrackingNumber>
              <h3>Tracking Details</h3>
              <p>{trackingData.trackingNumber}</p>
            </TrackingNumber>
            <StatusBadge variant={getTrackingStatusVariant(trackingData.status)} label={trackingData.status} />
          </CardHeader>

          <InfoGrid>
            <InfoItem>
              <InfoLabel>Carrier</InfoLabel>
              <InfoValue>{trackingData.carrier}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Origin</InfoLabel>
              <InfoValue>{trackingData.origin}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Destination</InfoLabel>
              <InfoValue>{trackingData.destination}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Current Location</InfoLabel>
              <InfoValue>{trackingData.currentLocation}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Estimated Delivery</InfoLabel>
              <InfoValue>{new Date(trackingData.estimatedDelivery).toLocaleDateString()}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Status</InfoLabel>
              <InfoValue>{trackingData.status}</InfoValue>
            </InfoItem>
          </InfoGrid>

          <div style={{ marginTop: '32px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '24px', color: '#1e293b' }}>
              Tracking History
            </h3>
            <Timeline>
              {trackingData.events.map((event, index) => (
                <TimelineItem
                  key={index}
                  $isFirst={index === 0}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  <EventTime>{event.timestamp}</EventTime>
                  <EventLocation>{event.location}</EventLocation>
                  <EventDescription>
                    <strong>{event.status}:</strong> {event.description}
                  </EventDescription>
                </TimelineItem>
              ))}
            </Timeline>
          </div>
        </TrackingCard>
      )}
    </TrackingContainer>
  );
};
